/**
 * Shared PDP selection inventory fetch (IM-004 / IM-005).
 * Single React Query key for warehouse-scoped getProducts by selection product IDs.
 */

import {useMemo} from 'react';

import {useProducts} from '@amblique/king-living/app/hooks/use-products';
import {usePdpShopperInventoryIds} from '@amblique/king-living/app/hooks/use-pdp-shopper-inventory-ids';
import {GET_PRODUCTS_PARAMS} from '@amblique/king-living/app/utils/api-utils';
import {collectPdpLineItemProductIds} from '@amblique/king-living/app/utils/cart-utils';
import {getSelectedVariantProduct} from '@amblique/king-living/app/utils/customize-selections';
import {
  getInventoryProductIdFromVariant,
  isInventoryFamilyA,
} from '@amblique/king-living/app/utils/inventory-utils';

/**
 * ATS moves slowly relative to a PDP session; caching for a minute stops background
 * refetches (window focus, remount, revisiting a selection) from re-gating the CTA.
 */
const PDP_INVENTORY_STALE_TIME_MS = 60 * 1000;

/**
 * Stable, sorted SCAPI product IDs for a PDP selection inventory batch.
 *
 * @param {Object|null|undefined} selections
 * @param {Object} [options]
 * @param {Object|null|undefined} [options.variant]
 * @param {Object|null|undefined} [options.product]
 * @returns {string[]}
 */
export function buildPdpInventoryProductIds(selections, {variant, product} = {}) {
  const ids = new Set(collectPdpLineItemProductIds(selections));
  const coverId = selections?.cover?.productId ?? selections?.cover?.variantId;
  const primaryProductId =
    coverId ?? variant?.productId ?? product?.id ?? product?.productId ?? null;

  if (primaryProductId) {
    ids.add(primaryProductId);
  }

  // Family A only: fetch the cover’s linked FG so warehouse inventoryIds
  // apply to the stock subject. Family B leftover inventoryProductId must
  // not enter the batch — the generic OOS / Notify Me loops would treat
  // that FG as a selected line and block MTO ATC (DE-819).
  if (coverId && product && isInventoryFamilyA(product)) {
    const coverEntry = getSelectedVariantProduct(product, coverId);
    const fgId = getInventoryProductIdFromVariant(coverEntry);

    if (fgId) {
      ids.add(fgId);
    }
  }

  return [...ids].filter(Boolean).sort();
}

/**
 * @param {Object} options
 * @param {string[]} options.productIds - Sorted unique SCAPI product IDs
 * @param {boolean} [options.enabled=true]
 * @returns {{
 *   productIds: string[],
 *   products: Array,
 *   productsById: Record<string, Object>,
 *   isPending: boolean,
 *   isError: boolean,
 * }}
 */
export function usePdpSelectionInventoryProducts({productIds, enabled = true}) {
  const {shopperInventoryIds, isInventoryIdsResolving} = usePdpShopperInventoryIds();
  const idsKey = productIds.length > 0 ? productIds.join(',') : '';
  const shouldFetch = enabled && productIds.length > 0 && !isInventoryIdsResolving;

  const {data, isLoading, isError} = useProducts(
    {
      parameters: {
        ...GET_PRODUCTS_PARAMS,
        ids: idsKey,
      },
    },
    {
      enabled: shouldFetch,
      staleTime: PDP_INVENTORY_STALE_TIME_MS,
      // getProducts can 504 under load. Extra retries with backoff outlast the
      // CTA wait window; one retry rides out a blip, then callers fall back to
      // site-default inventory instead of treating an empty error as OOS.
      retry: 1,
    },
    {inventoryIds: shopperInventoryIds}
  );

  const products = data?.data ?? [];

  const productsById = useMemo(() => {
    const map = {};

    products.forEach((item) => {
      if (item?.id) {
        map[item.id] = item;
      }
    });

    return map;
  }, [products]);

  // Only the first load blocks the CTA. Background refetches keep serving cached ATS
  // rather than flipping the button back to a spinner.
  const isPending =
    (enabled && isInventoryIdsResolving) ||
    (shouldFetch && isLoading);

  // Exhausted query with no payload (failed refetch that still has cache is
  // not an error — keep using the cached products).
  const isFetchError = Boolean(shouldFetch && isError && !data);

  return {
    isError: isFetchError,
    isPending,
    productIds,
    products,
    productsById,
  };
}

export default usePdpSelectionInventoryProducts;
